/**
 * Entra group membership kind — stored on `entra_group_members.member_type`.
 *
 * Phase-1 sync only resolves user-typed members (the `fetchGroupMembers`
 * Graph call filters on `microsoft.graph.user`). Reserved values for guest
 * and nested-group members land here when those paths are added.
 */
export enum EntraGroupMemberType {
	USER = 'user',
	GUEST = 'guest',
	GROUP = 'group',
}

/**
 * Entra entity type - type of directory object from Microsoft Entra ID / M365.
 * Classified using MS Graph `mailboxSettings.userPurpose` for users
 * and `mailEnabled`/`securityEnabled`/`groupTypes` for groups.
 */
export enum EntraEntityType {
	/** Regular member user (userType: Member, userPurpose: user) */
	USER = 'user',
	/** External/guest user (userType: Guest) */
	GUEST = 'guest',
	/** Security group or Microsoft 365 group */
	GROUP = 'group',
	/** Mail-enabled distribution group */
	DISTRIBUTION_LIST = 'distribution_list',
	/** Shared mailbox (userPurpose: shared) */
	SHARED_MAILBOX = 'shared_mailbox',
	/** Room resource mailbox (userPurpose: room) */
	ROOM = 'room',
	/** Equipment resource mailbox (userPurpose: equipment) */
	EQUIPMENT = 'equipment',
	/** Unclassified entity */
	UNKNOWN = 'unknown',
}

/**
 * Assigned license from Microsoft 365.
 * Represents a single license SKU assigned to a user.
 */
export interface IAssignedLicense {
	skuId: string
	disabledPlans: string[]
}

/**
 * Entra Directory Entity - read-only mirror of a Microsoft Entra ID / M365 directory object.
 *
 * This replaces the old IPerson interface. The table is populated exclusively
 * by the Entra sync job — there is no manual create/update via HTTP API.
 *
 * Entities include users, guests, groups, distribution lists, shared mailboxes,
 * rooms, and equipment.
 */
export interface IEntraDirectoryEntity {
	entraDirectoryId: string
	entraIdObjectId: string
	email: string | null
	displayName: string
	firstName?: string
	lastName?: string
	userPrincipalName?: string
	userType?: string

	// Entity classification
	entityType: EntraEntityType

	// Organizational data (read-only from Entra)
	jobTitle?: string
	department?: string
	companyName?: string
	officeLocation?: string
	employeeId?: string
	employeeType?: string
	employeeHireDate?: Date

	// Sign-in activity
	accountEnabled: boolean
	lastSignInAt?: Date | null
	lastNonInteractiveSignInAt?: Date | null
	lastSuccessfulSignInAt?: Date | null

	// Licenses
	assignedLicenses?: IAssignedLicense[] | null

	// Contact info
	businessPhones?: string[] | null
	mobilePhone?: string

	// Location
	city?: string
	country?: string
	usageLocation?: string

	// Entra metadata
	entraCreatedAt?: Date | null

	// Sync metadata
	status: 'active' | 'disabled' | 'pending_deletion' | 'deleted'
	syncHash?: string
	syncedAt?: Date
	pendingDeletionAt?: Date

	// Standard timestamps
	createdAt: Date
	updatedAt: Date
	deletedAt?: Date | null
}
